// Cphere Request & Correlation Foundation — browser operation correlation.
//
// KEPT BYTE-IDENTICAL between superadmin and client (like noStoreRequest.js).
// Any change here must be mirrored verbatim in the sibling repository.
//
// One correlation id represents ONE logical user operation: a Campaign Setup
// submission attempt including its ambiguous retries, one deployment-plan
// confirmation, one report generation. It is:
//   - generated with WebCrypto only (fail closed — never Math.random, never
//     Date.now, never derived from form hashes, idempotency keys, action ids
//     or user/advertiser ids),
//   - held in active in-memory operation state only — never persisted to
//     localStorage, sessionStorage, IndexedDB, cookies or URLs,
//   - attached as the X-Cphere-Correlation-Id request header only when a
//     caller supplies the active operation's correlation id (never one
//     static value across unrelated traffic),
//   - completely separate from the request-idempotency key: same shape,
//     never the same value, never derived from one another.
//
// The backend always answers with the authoritative X-Cphere-Request-Id
// (server-generated, unique per HTTP attempt) and X-Cphere-Correlation-Id
// (the browser value when accepted, otherwise a server replacement). The
// request id is the opaque support reference safe to show in error copy.

export const CORRELATION_ID_HEADER = "X-Cphere-Correlation-Id";
export const REQUEST_ID_HEADER = "X-Cphere-Request-Id";

// Mirrors the backend's strict trace-id contract (utils/requestContext.js).
export const TRACE_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

export function isValidTraceId(value) {
  return typeof value === "string" && TRACE_ID_PATTERN.test(value);
}

/**
 * Generate one operation correlation id. WebCrypto only; fails closed when
 * no cryptographically strong source exists (callers may then proceed
 * WITHOUT correlation — tracing is best-effort and must never block the
 * user's operation). The cryptoApi parameter exists for deterministic tests.
 */
export function generateOperationCorrelationId(cryptoApi) {
  const cryptoSource =
    cryptoApi || (typeof window !== "undefined" ? window.crypto : undefined);

  if (cryptoSource && typeof cryptoSource.randomUUID === "function") {
    const uuid = cryptoSource.randomUUID();
    if (isValidTraceId(uuid)) return uuid;
  }

  if (cryptoSource && typeof cryptoSource.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoSource.getRandomValues(bytes);
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    if (isValidTraceId(hex)) return hex;
  }

  throw new Error(
    "Secure correlation id generation is unavailable (WebCrypto missing)."
  );
}

/**
 * Merge the active operation correlation id into a cloned Headers instance.
 * Every legal HeadersInit form is supported and caller-owned input is never
 * mutated. Invalid supplied or pre-existing correlation values are omitted.
 */
export function withCorrelationHeader(headers, correlationId) {
  const base = new Headers(headers || undefined);
  if (isValidTraceId(correlationId)) {
    base.set(CORRELATION_ID_HEADER, correlationId);
  } else if (
    correlationId !== undefined ||
    (base.has(CORRELATION_ID_HEADER) && !isValidTraceId(base.get(CORRELATION_ID_HEADER)))
  ) {
    base.delete(CORRELATION_ID_HEADER);
  }
  return base;
}

/**
 * Read the authoritative trace headers from a fetch Response. Values are
 * validated before use; anything malformed reads as null.
 */
export function readTraceHeadersFromResponse(response) {
  const headers = response && response.headers;
  const get =
    headers && typeof headers.get === "function" ? headers.get.bind(headers) : () => null;
  const requestId = get(REQUEST_ID_HEADER);
  const correlationId = get(CORRELATION_ID_HEADER);
  return {
    requestId: isValidTraceId(requestId) ? requestId : null,
    correlationId: isValidTraceId(correlationId) ? correlationId : null,
  };
}

/**
 * Attach safe trace metadata to a normalized transport error so error
 * surfaces can show an opaque support reference without re-parsing headers.
 */
export function attachTraceToError(error, response) {
  if (!error || typeof error !== "object") return error;
  const { requestId, correlationId } = readTraceHeadersFromResponse(response);
  if (requestId && error.requestId === undefined) error.requestId = requestId;
  if (correlationId && error.correlationId === undefined) error.correlationId = correlationId;
  return error;
}

/**
 * Restrained operator-reference copy for unexpected failures. Only the
 * opaque server request id is ever shown — never the correlation id, never
 * stack traces or internal messages.
 */
export function formatSupportReference(requestId) {
  return isValidTraceId(requestId) ? `Reference: ${requestId}` : null;
}
